/**
 * Sections & Tables Thunks
 * Async thunks for section and table management operations
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  localFetchOutletSections,
  localFetchOutletTables,
} from '@/renderer/lib/powersync/localOrderOps';
import {
  createSection as createSectionService,
  createTable as createTableService,
  fetchOutletSections,
  fetchOutletTables,
  updateSection as updateSectionService,
  updateTable as updateTableService,
} from '../../services/sectionService';
import {
  CreateSectionRequest,
  CreateTableRequest,
  OutletSection,
  OutletTable,
  UpdateSectionRequest,
  UpdateTableRequest,
} from '../../types/section';

/**
 * Fetch all sections for the current outlet
 */
export const fetchSections = createAsyncThunk<
  OutletSection[],
  string | null | undefined,
  { rejectValue: string }
>('sections/fetchSections', async (outletId, { rejectWithValue }) => {
  if (!outletId) {
    // Company-wide view (e.g. a super user viewing "All Outlets") — local
    // PowerSync SQLite only ever holds one outlet's data at a time, so fall
    // back to the existing Supabase-backed fetch (RLS already scopes it to
    // the caller's own company).
    const response = await fetchOutletSections(null);
    if (!response.success) {
      return rejectWithValue(response.error || 'Failed to fetch sections');
    }
    return response.data ?? [];
  }
  try {
    return await localFetchOutletSections(outletId);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch sections',
    );
  }
});

/**
 * Create a new section
 */
export const createSectionThunk = createAsyncThunk<
  OutletSection,
  CreateSectionRequest,
  { rejectValue: string }
>('sections/createSection', async (request, { rejectWithValue }) => {
  try {
    const response = await createSectionService(request);
    if (!response.success || !response.data) {
      return rejectWithValue(response.error || 'Failed to create section');
    }
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to create section',
    );
  }
});

/**
 * Update a section
 */
export const updateSectionThunk = createAsyncThunk<
  OutletSection,
  UpdateSectionRequest,
  { rejectValue: string }
>('sections/updateSection', async (request, { rejectWithValue }) => {
  try {
    const response = await updateSectionService(request);
    if (!response.success || !response.data) {
      return rejectWithValue(response.error || 'Failed to update section');
    }
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to update section',
    );
  }
});

/**
 * Fetch all tables for the current outlet
 */
export const fetchTables = createAsyncThunk<
  OutletTable[],
  string | null | undefined,
  { rejectValue: string }
>('sections/fetchTables', async (outletId, { rejectWithValue }) => {
  if (!outletId) {
    // Company-wide view (e.g. a super user viewing "All Outlets") — local
    // PowerSync SQLite only ever holds one outlet's data at a time, so fall
    // back to the existing Supabase-backed fetch (RLS already scopes it to
    // the caller's own company).
    const response = await fetchOutletTables(null);
    if (!response.success) {
      return rejectWithValue(response.error || 'Failed to fetch tables');
    }
    return response.data ?? [];
  }
  try {
    return await localFetchOutletTables(outletId);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch tables',
    );
  }
});

/**
 * Create a new table
 */
export const createTableThunk = createAsyncThunk<
  OutletTable,
  CreateTableRequest,
  { rejectValue: string }
>('sections/createTable', async (request, { rejectWithValue }) => {
  try {
    const response = await createTableService(request);
    if (!response.success || !response.data) {
      return rejectWithValue(response.error || 'Failed to create table');
    }
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to create table',
    );
  }
});

/**
 * Update a table
 */
export const updateTableThunk = createAsyncThunk<
  OutletTable,
  UpdateTableRequest,
  { rejectValue: string }
>('sections/updateTable', async (request, { rejectWithValue }) => {
  try {
    const response = await updateTableService(request);
    if (!response.success || !response.data) {
      return rejectWithValue(response.error || 'Failed to update table');
    }
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to update table',
    );
  }
});
